"use client";

import { useEffect, useRef, useState } from "react";
import {
  MAX_CHAT_ATTACHMENT_BYTES,
  MAX_CHAT_ATTACHMENT_NAME_LENGTH,
  isAllowedChatAttachmentName,
  resolveBriefAttachmentContentType,
  toast,
  type BriefAttachmentContentType,
  type ChatAttachment,
} from "@drpcrd/utils";
import type { ChatAttachmentUploadTarget } from "../../chat-attachments/storage";

export type { ChatAttachmentUploadTarget };

type Options = {
  /** Issues a signed PUT URL for the chosen content type. */
  requestUploadTarget: (
    fileType: BriefAttachmentContentType,
  ) => Promise<ChatAttachmentUploadTarget>;
  /** Deletes a draft object from GCS (X / replace / unmount). */
  deleteUploadedAttachment?: (key: string) => Promise<void>;
  /**
   * Whether the message with this draft is being sent. Leaving the page then
   * keeps the file, since it is on its way with the message.
   */
  isSending?: boolean;
};

/**
 * Single-file upload for the deal-memo activity chat input: pick → spinner
 * chip → ready chip (or clear). Replacing or removing a draft deletes the
 * GCS object; clearing after a successful send does not.
 */
export const useChatAttachmentUpload = ({
  requestUploadTarget,
  deleteUploadedAttachment,
  isSending = false,
}: Options) => {
  const [pendingName, setPendingName] = useState<string | null>(null);
  const [attachment, setAttachment] = useState<ChatAttachment | null>(null);
  /** Key issued for the in-flight or ready draft, so X can delete it. */
  const draftKeyRef = useRef<string | null>(null);
  /** Aborts the in-flight upload when the sender cancels or picks another file. */
  const uploadAbortRef = useRef<AbortController | null>(null);

  /** The latest props, for the unmount clean-up below, which runs once. */
  const latestRef = useRef({ deleteUploadedAttachment, isSending });
  useEffect(() => {
    latestRef.current = { deleteUploadedAttachment, isSending };
  });

  const abortInFlightUpload = () => {
    uploadAbortRef.current?.abort();
    uploadAbortRef.current = null;
  };

  const deleteDraftObject = async (key: string | null) => {
    const { deleteUploadedAttachment: deleteUploaded } = latestRef.current;
    if (!key || !deleteUploaded) {
      return;
    }
    try {
      await deleteUploaded(key);
    } catch {
      // Nothing the sender can do about it: a draft that was never sent is
      // left unstamped, so it can be expired later.
    }
  };

  // Leaving the page with a draft stops the upload and deletes the file. A
  // sent file is safe: `clearAttachment` drops the key once the send succeeds,
  // and while a send is still in flight the file is left alone.
  useEffect(
    () => () => {
      abortInFlightUpload();
      if (!latestRef.current.isSending) {
        void deleteDraftObject(draftKeyRef.current);
      }
    },
    [],
  );

  /** Clears local draft state without touching GCS (after a successful send). */
  const clearAttachment = () => {
    draftKeyRef.current = null;
    setPendingName(null);
    setAttachment(null);
  };

  /** X / cancel: drop the draft and delete the uploaded object from GCS. */
  const removeAttachment = () => {
    abortInFlightUpload();
    const key = draftKeyRef.current;
    draftKeyRef.current = null;
    setPendingName(null);
    setAttachment(null);
    void deleteDraftObject(key);
  };

  const onFileSelected = async (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!file) {
      return;
    }

    const contentType = resolveBriefAttachmentContentType(file);
    if (!contentType) {
      toast.error("That file type can’t be attached", {
        description: "Use PDF, JPG, PNG, GIF, DOC/DOCX, XLS/XLSX, or CSV",
      });
      return;
    }

    if (file.size > MAX_CHAT_ATTACHMENT_BYTES) {
      toast.error("That file is too large", {
        description: "Attachments must be 10 MB or smaller",
      });
      return;
    }

    // Same check the server runs on send, so the sender hears about it now
    // rather than as a failed send.
    if (!isAllowedChatAttachmentName(file.name)) {
      toast.error("That file name can’t be attached", {
        description: `Rename it to end in .pdf, .jpg, .png, .gif, .doc, .docx, .xls, .xlsx or .csv, and keep it under ${MAX_CHAT_ATTACHMENT_NAME_LENGTH} characters`,
      });
      return;
    }

    abortInFlightUpload();
    const controller = new AbortController();
    uploadAbortRef.current = controller;

    const previousKey = draftKeyRef.current;
    draftKeyRef.current = null;
    setAttachment(null);
    setPendingName(file.name);
    void deleteDraftObject(previousKey);

    try {
      const target = await requestUploadTarget(contentType);
      if (controller.signal.aborted) {
        // Cancelled before the PUT started, so nothing was written.
        return;
      }
      draftKeyRef.current = target.key;
      const res = await fetch(target.url, {
        method: "PUT",
        body: file,
        // Besides the content type, the headers the URL was signed with,
        // which carry the size cap: a PUT without them is refused.
        headers: { "Content-Type": contentType, ...target.headers },
        signal: controller.signal,
      });
      if (!res.ok) {
        throw new Error(
          `GCS upload failed with ${res.status} ${res.statusText}`,
        );
      }
      setAttachment({ id: target.key, name: file.name, provider: "gcs" });
    } catch {
      if (controller.signal.aborted) {
        // Cancelled by X or a new pick, which already cleaned up the draft.
        return;
      }
      // Usually a CORS rejection on the signed PUT (no status), or a GCS
      // 4xx when Content-Type / credentials don't match the signed URL.
      const failedKey = draftKeyRef.current;
      draftKeyRef.current = null;
      void deleteDraftObject(failedKey);
      toast.error(`Couldn’t upload ${file.name}`, {
        description: "Please try again",
      });
    } finally {
      // A newer pick owns the spinner from here on.
      if (uploadAbortRef.current === controller) {
        uploadAbortRef.current = null;
        setPendingName(null);
      }
    }
  };

  return {
    pendingName,
    attachment,
    isUploading: pendingName != null,
    clearAttachment,
    removeAttachment,
    onFileSelected,
  };
};
